import { useState } from "react";
import { Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { showcaseCodesMatch, storeShowcaseAccess } from "@/utils/showcaseAccess";
import { getBrandLogoStyle } from "@/lib/brand/logoScale";

interface ShowcaseAccessGateProps {
  organizationId: string;
  organizationName: string;
  organizationLogoUrl?: string | null;
  logoScale?: number;
  expectedCode: string | null | undefined;
  onUnlocked: () => void;
}

export const ShowcaseAccessGate = ({
  organizationId,
  organizationName,
  organizationLogoUrl,
  logoScale,
  expectedCode,
  onUnlocked,
}: ShowcaseAccessGateProps) => {
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (showcaseCodesMatch(value, expectedCode)) {
      storeShowcaseAccess(organizationId, value);
      setError(null);
      onUnlocked();
      return;
    }
    setError("That access code isn't correct. Please check it and try again.");
  };

  return (
    <div className="brand-showcase min-h-screen bg-background flex items-center justify-center px-4">
      <div className="showcase-panel w-full max-w-md text-center space-y-6">
        {organizationLogoUrl ? (
          <img
            src={organizationLogoUrl}
            alt={`${organizationName} logo`}
            className={logoScale ? "brand-showcase-gate-logo mx-auto object-contain" : "h-20 w-20 mx-auto object-contain rounded-lg"}
            style={logoScale ? getBrandLogoStyle(logoScale) : undefined}
          />
        ) : (
          <div className="h-16 w-16 mx-auto rounded-full bg-muted flex items-center justify-center">
            <Lock className="h-7 w-7 text-muted-foreground" />
          </div>
        )}

        <div className="space-y-2">
          <h1 className="showcase-heading text-2xl md:text-3xl font-bold">
            {organizationName}
          </h1>
          <p className="text-muted-foreground">
            This showcase is protected. Enter the access code to continue.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <Input
            autoFocus
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              if (error) setError(null);
            }}
            placeholder="Access code"
            aria-label="Access code"
            className="text-center font-mono tracking-[0.3em] uppercase h-12 text-lg"
          />
          {error && <p className="text-sm text-destructive">{error}</p>}
          <Button type="submit" className="w-full h-11" disabled={!value.trim()}>
            Continue
          </Button>
        </form>

        <p className="text-xs text-muted-foreground">
          Don't have a code? Please contact {organizationName} directly.
        </p>

        <div className="pt-6 border-t">
          <a
            href="https://www.snapshot.live"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            Powered by Snapshot Live
          </a>
        </div>
      </div>
    </div>
  );
};
